import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BackBar from '../components/BackBar';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('opwmp_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Parameter labels for display — adapted for VIC
const PARAMETER_LABELS = [
  { key: 'residential_waste_streams', label: 'Residential Waste Streams', group: 'Waste Streams' },
  { key: 'commercial_waste_streams', label: 'Commercial Waste Streams', group: 'Waste Streams' },
  { key: 'generation_rates_basis', label: 'Generation Rates Basis', group: 'Rates' },
  { key: 'collection_frequency', label: 'Collection Frequency', group: 'Collection' },
  { key: 'collection_method', label: 'Collection Method', group: 'Collection' },
  { key: 'bulky_waste_sizing_residential', label: 'Bulky Waste Sizing (Residential)', group: 'Bulky Waste' },
  { key: 'bulky_waste_sizing_commercial', label: 'Bulky Waste Sizing (Commercial)', group: 'Bulky Waste' },
  { key: 'chute_allowances', label: 'Chute Allowances', group: 'Chute' },
  { key: 'chute_streams', label: 'Chute Streams', group: 'Chute' },
  { key: 'chute_height_threshold', label: 'Chute Height Threshold', group: 'Chute' },
  { key: 'accepted_bin_sizes', label: 'Accepted Bin Sizes', group: 'Bins' },
  { key: 'kerbside_requirements', label: 'Kerbside Requirements', group: 'Collection' },
  { key: 'manual_transport_distance', label: 'Manual Transport Distance', group: 'Transport' },
  { key: 'storage_to_kerbside_distance', label: 'Storage to Kerbside Distance', group: 'Transport' },
  { key: 'interim_storage_rules', label: 'Interim Storage Rules', group: 'Storage' },
  { key: 'fogo_mandates', label: 'FOGO Mandates', group: 'Compliance' },
  { key: 'glass_service', label: 'Glass Collection Service', group: 'Collection' },
  { key: 'compactor_requirements', label: 'Compactor Requirements', group: 'Equipment' },
  { key: 'planning_reference', label: 'Planning Reference', group: 'Reference' },
  { key: 'private_contractor_allowed', label: 'Private Contractor Allowed', group: 'Collection' },
];

const AdminVICCouncils = () => {
  const navigate = useNavigate();
  const [councils, setCouncils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCouncil, setSelectedCouncil] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  const fetchCouncils = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/vic-councils`, {
        headers: getAuthHeader()
      });
      setCouncils(response.data);
    } catch (err) {
      console.error('Failed to fetch VIC councils:', err);
      if (err.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCouncils();
  }, [fetchCouncils]);

  const filteredCouncils = councils.filter(c =>
    c.council_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditCouncil = (council) => {
    setEditData({ ...council });
    setSelectedCouncil(council);
    setShowEditModal(true);
    setError('');
    setSuccessMsg('');
  };

  const handleSaveCouncil = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      await axios.post(`${API}/api/admin/vic-councils`, editData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      setSuccessMsg('Council data saved successfully');
      setShowEditModal(false);
      fetchCouncils();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save council data');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/vic-councils-export-csv`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vic_councils_data.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export CSV');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getDataCompleteness = (council) => {
    if (!council.has_data) return 0;
    let filled = 0;
    PARAMETER_LABELS.forEach(p => {
      if (council[p.key] && council[p.key] !== 'TBC') filled++;
    });
    return Math.round((filled / PARAMETER_LABELS.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500" data-testid="vic-councils-loading">Loading councils...</div>
      </div>
    );
  }

  const dataFilledCount = councils.filter(c => c.has_data).length;

  // Parse VIC generation rates from the nested dict
  const parseVICRates = (council) => {
    const rates = council.residential_generation_rates;
    if (!rates) return null;
    const onebed = rates['1bed_studio'];
    const twobed = rates['2bed'];
    const threebed = rates['3bed_plus'];
    if (!onebed && !twobed && !threebed) return null;
    return { onebed, twobed, threebed };
  };

  // Parse VIC waste streams into badges
  const parseStreams = (text) => {
    const items = [];
    const t = text.toLowerCase();
    if (t.includes('garbage') || t.includes('general waste')) items.push({ name: 'Garbage', lid: t.includes('green lid') && !t.includes('red lid') ? 'green' : 'red', color: t.includes('green lid') && !t.includes('red lid') ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200' });
    if (t.includes('recycling') || t.includes('commingled')) items.push({ name: 'Recycling', lid: 'yellow', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' });
    if (t.includes('organics') || t.includes('fogo') || t.includes('green waste')) items.push({ name: 'FOGO', lid: 'lime', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' });
    if (t.includes('glass')) items.push({ name: 'Glass', lid: 'purple', color: 'bg-purple-100 text-purple-700 border-purple-200' });
    return items;
  };

  // Parse chute type
  const parseChuteType = (text) => {
    const t = (text || '').toLowerCase();
    if (t.includes('n/a') || t.includes('not applicable') || !text) return { type: 'none', label: 'N/A' };
    if (t.includes('dual chute') || t.includes('dual') || t.includes('garbage + recycling') || t.includes('garbage and recycling') || t.includes('twin chute')) return { type: 'gw+recy', label: 'GW + Recycling' };
    if (t.includes('permitted') || t.includes('per sv') || t.includes('chute')) return { type: 'sv', label: 'Per SV Guide' };
    return { type: 'unknown', label: 'Per WMP' };
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="vic-councils-page">
      <BackBar to="/admin" label="Admin" />
      {/* Header */}
      <header className="bg-gray-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold">VIC Council Reference Tool</h1>
              <p className="text-gray-400 text-sm">Waste management parameters for 31 Melbourne metro councils</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400">
                {dataFilledCount}/{councils.length} councils with data
              </span>
              <button
                onClick={handleExportCSV}
                className="text-sm text-gray-300 hover:text-white px-3 py-1.5 rounded border border-gray-600 hover:border-gray-400"
                data-testid="export-csv-btn"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & View Toggle */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search councils..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              data-testid="council-search-input"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
              data-testid="view-grid-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
              data-testid="view-table-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error / Success */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="error-message">{error}</div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm" data-testid="success-message">{successMsg}</div>
        )}

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="councils-grid">
            {filteredCouncils.map((council) => {
              const streams = council.residential_waste_streams || '';
              const streamItems = parseStreams(streams);
              const chuteInfo = parseChuteType(council.chute_allowances);
              const vicRates = parseVICRates(council);
              const isVerified = (council.notes || '').includes('VERIFIED');
              const binColours = council.bin_colours;

              return (
                <div
                  key={council.council_name}
                  className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                  data-testid={`council-card-${council.council_name.replace(/\s/g, '-').toLowerCase()}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 text-sm leading-tight pr-2">
                      {council.council_name}
                    </h3>
                    {isVerified ? (
                      <span className="shrink-0 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">Verified</span>
                    ) : council.has_data ? (
                      <span className="shrink-0 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Data</span>
                    ) : (
                      <span className="shrink-0 text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-medium">TBC</span>
                    )}
                  </div>

                  {council.has_data && (
                    <div className="space-y-3 mb-3">
                      {/* Waste Streams */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Waste Streams</p>
                        <div className="flex flex-wrap gap-1">
                          {streamItems.map((s, i) => (
                            <span key={i} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${s.color}`}>
                              <span className={`w-2 h-2 rounded-full ${s.lid === 'red' ? 'bg-red-500' : s.lid === 'yellow' ? 'bg-yellow-500' : s.lid === 'lime' ? 'bg-lime-500' : s.lid === 'purple' ? 'bg-purple-500' : 'bg-green-500'}`} />
                              {s.name}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Bin Colours (if available) */}
                      {binColours && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Bin Colours</p>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(binColours).map(([stream, colour]) => (
                              <span key={stream} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                                {stream}: {colour}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Generation Rates */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Generation Rates (L/unit/wk)</p>
                        {vicRates ? (
                          <div className="flex flex-wrap gap-1">
                            {vicRates.onebed && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">
                                1bed: GW {vicRates.onebed.garbage_l_week}L / R {vicRates.onebed.recycling_l_week}L
                              </span>
                            )}
                            {vicRates.twobed && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700">
                                2bed: GW {vicRates.twobed.garbage_l_week}L / R {vicRates.twobed.recycling_l_week}L
                              </span>
                            )}
                            {vicRates.threebed && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">
                                3bed+: GW {vicRates.threebed.garbage_l_week}L / R {vicRates.threebed.recycling_l_week}L
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400 italic">Per SV standard</span>
                        )}
                      </div>

                      {/* Collection Frequency */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Collection</p>
                        <p className="text-[10px] text-gray-600 leading-relaxed">{council.collection_frequency || 'Per council schedule'}</p>
                      </div>

                      {/* Chute Allowances */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Chute</p>
                        <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          chuteInfo.type === 'gw+recy' ? 'bg-teal-100 text-teal-800' :
                          chuteInfo.type === 'sv' ? 'bg-blue-100 text-blue-800' :
                          chuteInfo.type === 'none' ? 'bg-gray-100 text-gray-500' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {chuteInfo.label}
                        </span>
                        {council.chute_height_threshold && (
                          <span className="ml-1 text-[10px] text-gray-400">{council.chute_height_threshold}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => handleEditCouncil(council)}
                      className="flex-1 text-xs font-medium px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                      data-testid={`edit-council-${council.council_name.replace(/\s/g, '-').toLowerCase()}`}
                    >
                      {council.has_data ? 'View / Edit' : 'Add Data'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="councils-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Council</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Waste Streams</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">1-Bed Rates</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">2-Bed Rates</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Chute</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCouncils.map((council) => {
                    const streamItems = parseStreams(council.residential_waste_streams || '');
                    const chuteInfo = parseChuteType(council.chute_allowances);
                    const vicRates = parseVICRates(council);

                    return (
                      <tr key={council.council_name} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">{council.council_name}</span>
                          {(council.notes || '').includes('VERIFIED') && (
                            <span className="ml-1.5 text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium align-middle">Verified</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {streamItems.map((s, i) => (
                              <span key={i} className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.color}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${s.lid === 'red' ? 'bg-red-500' : s.lid === 'yellow' ? 'bg-yellow-500' : s.lid === 'lime' ? 'bg-lime-500' : s.lid === 'purple' ? 'bg-purple-500' : 'bg-green-500'}`} />
                                {s.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {vicRates?.onebed ? (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">
                              GW {vicRates.onebed.garbage_l_week}L / R {vicRates.onebed.recycling_l_week}L
                            </span>
                          ) : <span className="text-[10px] text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {vicRates?.twobed ? (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700">
                              GW {vicRates.twobed.garbage_l_week}L / R {vicRates.twobed.recycling_l_week}L
                            </span>
                          ) : <span className="text-[10px] text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            chuteInfo.type === 'gw+recy' ? 'bg-teal-100 text-teal-800' :
                            chuteInfo.type === 'sv' ? 'bg-blue-100 text-blue-800' :
                            chuteInfo.type === 'none' ? 'bg-gray-100 text-gray-500' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {chuteInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleEditCouncil(council)}
                            className="text-xs font-medium px-2.5 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                          >
                            {council.has_data ? 'Edit' : 'Add'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filteredCouncils.length === 0 && (
          <div className="text-center py-12 text-gray-500" data-testid="no-results">
            No councils match your search.
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-8 z-50 overflow-y-auto" data-testid="edit-council-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 mb-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-900" data-testid="modal-council-name">
                  {editData.council_name}
                </h2>
                <p className="text-sm text-gray-500">VIC Waste Management Parameters</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-modal-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaveCouncil}>
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
                )}

                {(() => {
                  const groups = {};
                  PARAMETER_LABELS.forEach((p, idx) => {
                    if (!groups[p.group]) groups[p.group] = [];
                    groups[p.group].push({ ...p, idx });
                  });

                  return Object.entries(groups).map(([groupName, params]) => (
                    <div key={groupName} className="space-y-3">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-1">
                        {groupName}
                      </h3>
                      {params.map((param) => (
                        <div key={param.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {param.idx + 1}. {param.label}
                          </label>
                          <textarea
                            value={editData[param.key] || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, [param.key]: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                            placeholder="TBC"
                            data-testid={`input-${param.key}`}
                          />
                        </div>
                      ))}
                    </div>
                  ));
                })()}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={editData.notes || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Additional notes..."
                    data-testid="input-notes"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="save-council-btn"
                >
                  {saving ? 'Saving...' : 'Save Parameters'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVICCouncils;
